<template>
  <div class="bg-white rounded-2xl border-2 border-black shadow-[6px_6px_0px_#000] p-6">
    <!-- Profile Header -->
    <div class="text-center mb-6">
      <UserAvatar 
        :src="tutor?.profileImage" 
        :name="tutor?.name || 'Tutor'" 
        size="xl"
        class="mx-auto mb-4 border-2 border-black shadow-[3px_2px_1px_rgba(0,0,0,1)]"
      />
      <h2 class="text-xl font-semibold text-gray-900">{{ tutor?.name }}</h2>
      <div class="flex items-center justify-center mt-2">
        <StarIcon class="w-4 h-4 text-yellow-400 mr-1" />
        <span class="text-sm text-gray-600">
          {{ tutor?.rating }}
        </span>
      </div>
    </div>

    <!-- Subjects -->
    <div class="mb-6">
      <h3 class="section-label">SUBJECTS</h3>
      <div class="flex flex-wrap gap-2">
        <span
          v-for="subject in (tutor?.subjects || defaultSubjects)"
          :key="subject"
          class="subject-tag"
          :title="subject"
        >
          {{ truncateSubject(subject) }}
        </span>
      </div>
    </div>

    <!-- Education -->
    <div class="mb-6">
      <h3 class="section-label">EDUCATION</h3>
      <p class="section-value">{{ tutor?.education }}</p>
    </div>

    <!-- Experience -->
    <div v-if="tutor?.experience" class="mb-6">
      <h3 class="section-label">EXPERIENCE</h3>
      <p class="section-value">
        {{ tutor.experience.startsWith('0 years') || tutor.experience.startsWith('0 year') 
            ? 'Just starting as a new tutor.' 
            : tutor.experience 
        }}
      </p>
    </div>

    <!-- Languages (hide if no data or empty array) -->
    <div v-if="tutor?.languages && tutor.languages.length > 0" class="mb-6">
      <h3 class="section-label">LANGUAGES</h3>
      <div class="flex flex-wrap gap-2">
        <span
          v-for="language in tutor.languages"
          :key="language"
          class="language-tag"
        >
          {{ language }}
        </span>
      </div>
    </div>

    <!-- City -->
    <div v-if="tutor?.city" class="mb-6">
      <h3 class="section-label">SERVICE AREA</h3>
      <div class="flex items-center gap-2 text-gray-600">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
          <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
        </svg>
        <span class="text-sm">{{ tutor?.city }}</span>
      </div>
    </div>

    <!-- About -->
    <div>
      <h3 class="section-label">ABOUT</h3>
      <p class="about-text">
        {{ tutor?.about }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { StarIcon } from '@heroicons/vue/24/outline'
import UserAvatar from '@/components/UserAvatar.vue'

defineProps({
  tutor: {
    type: Object,
    default: null
  }
})

// Default data to match the image
const defaultSubjects = ['General Mathematics', 'Statistics and Probability', 'Pre-Calculus']

// Truncate long subject names to first 3 words
const truncateSubject = (subject) => {
  if (!subject) return subject
  const words = subject.split(' ')
  if (words.length > 3) {
    return words.slice(0, 3).join(' ')
  }
  return subject
}
</script>

<style scoped>
.section-label {
  font-size: 11px;
  font-weight: 700;
  color: #4c4c4c;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-value {
  font-size: 14px;
  color: #374151;
  line-height: 1.4;
}

.subject-tag {
  background: #ecf7ff;
  color: #193CB8;
  border: 1px solid #193CB8;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
}

.language-tag {
  background: #f3f4f6;
  color: #374151;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.about-text {
  font-size: 14px;
  line-height: 1.5;
  color: #374151;
}
</style>